import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { folderIcon } from "@progress/kendo-svg-icons";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import { FolderPickerDialog } from "@presentation/components/storageConfiguration/FolderPickerDialog";
import { isValidStoragePath, STORAGE_PATH_HELP_TEXT } from "@presentation/components/storageConfiguration/storagePathValidation";
import type { PathTypeSchema } from "@domain/schemas/BI";

interface EditStoragePathDialogProps {
  currentPath: string;
  pathType: PathTypeSchema;
  listFoldersAsync: (pathType: PathTypeSchema, folder: string) => Promise<string[]>;
  onClose: () => void;
  /**
  Calls the atomic `SetPathValue` endpoint for this level - if it fails, the old path is untouched,
  so the dialog just surfaces the error and the caller's state stays consistent with the server.
  */
  onSubmit: (newPath: string) => Promise<void>;
}

/**
Changes an existing storage path's value in place via the backend's atomic `SetPathValue` endpoint -
never removes/re-adds the row, so it can't trip `OverlappingPathForSameFacet` (an empty existing
path is an ancestor of everything) or the deployment-level ‘at least one row per facet’ floor.
*/
export const EditStoragePathDialog: React.FC<EditStoragePathDialogProps> = ({ currentPath, pathType, listFoldersAsync, onClose, onSubmit }) => {
  const { pushNotification } = useNotifications();
  const [path, setPath] = useState(currentPath);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBrowsing, setIsBrowsing] = useState(false);

  const canSubmit = path.trim() !== currentPath && isValidStoragePath(path.trim()) && !isSubmitting;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(path.trim());
      onClose();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not change this path.", "error", 10_000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Change path" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label htmlFor="edit-storage-path" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Path
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <Input id="edit-storage-path" style={{ flex: 1, minWidth: 0 }} value={path} onChange={(event) => setPath(String(event.value ?? ""))} />
            <Button type="button" fillMode="flat" svgIcon={folderIcon} title="Browse" aria-label="Browse" onClick={() => setIsBrowsing(true)} />
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{STORAGE_PATH_HELP_TEXT}</div>
        </div>
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={() => void handleSubmit()}>
          Save
        </Button>
      </DialogActionsBar>
      {isBrowsing && (
        <FolderPickerDialog
          pathType={pathType}
          initialFolder={path}
          listFoldersAsync={listFoldersAsync}
          onSelect={(folder) => {
            setPath(folder);
            setIsBrowsing(false);
          }}
          onClose={() => setIsBrowsing(false)}
        />
      )}
    </Dialog>
  );
};

export default EditStoragePathDialog;
